<template>
  <div class="products" :class="{ 'products--loading': !areProductsFetched }" ref="products">
    <transition-group mode="out-in" name="no-products-fade" appear>
      <LoadingIndicator
        v-if="!areProductsFetched"
        key="products-loader"
        class="products__loader"
        :type="LOADING_INDICATOR_TYPES.cloudCentered"
      />
      <div v-else-if="isRenderingNoResults" key="products-no-results" class="products__no-results">
        <h3>{{ $t('no_results_title') }}</h3>
        <p @click="clearFilters" v-html="$t('no_results_copy')"></p>
      </div>
      <template v-else>
        <Cards
          v-if="areProductsFetched"
          ref="cards"
          key="products-cards"
          :class="{ 'cards--explode-variants': isExplodedVariants }"
          :cards="shuffledProductsPromos"
          :imageFormat="CONTENTFUL_IMAGE_FORMAT.jpg"
          :isScrollDetectionEnabled="!isMobile"
          :genderToShow="activeGender"
          :onPromoCardView="handlePromoCardView"
          :onPromoCardClick="handlePromoCardClick"
          :context="$CONTEXTS.filterPage"
          @before-last-enter="beforeLastCardEnter"
        />
        <div class="products__load-more" key="products-load-more">
          <Button
            v-if="pageInfo.hasNextPage"
            class="products__load-more-btn"
            :color="BUTTON_STYLES.color.white"
            :height="isMobile ? BUTTON_STYLES.height.small : BUTTON_STYLES.height.medium"
            :isSubmitting="isFetchingMoreProducts"
            @click="loadMore"
          >
            {{ $t('load_more') }}
          </Button>
        </div>
      </template>
    </transition-group>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { waitFor } from '@/helpers/time'
import { CONTENTFUL_IMAGE_FORMAT } from '@/components/_shared/ImageContentful.vue'
import LoadingIndicator, {
  LOADING_INDICATOR_TYPES,
} from '@/components/_shared/LoadingIndicator.vue'
import Cards from '@/components/_shared/Cards/Cards.vue'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'

const PROMO_CARD_EVENT_PREFIX = 'promo-card--'

export default {
  name: 'Products',

  components: {
    Cards,
    LoadingIndicator,
    Button,
  },

  props: {
    isExplodedVariants: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      BUTTON_STYLES,
      CONTENTFUL_IMAGE_FORMAT,
      isMounted: false,
      LOADING_INDICATOR_TYPES,
    }
  },

  computed: {
    ...mapState('filter', [
      'activePrimaryFilters',
      'areProductsFetched',
      'pageInfo',
      'isFetchingMoreProducts',
    ]),

    ...mapGetters({
      shuffledProductsPromos: 'filter/shuffledProductsPromos',
      activeGender: 'filter/activeGender',
      isMobile: 'filter/isMobile',
    }),

    isRenderingNoResults() {
      return this.areProductsFetched && this.shuffledProductsPromos.length === 0
    },
  },

  watch: {
    isRenderingNoResults: {
      immediate: true,
      handler() {
        if (this.isRenderingNoResults) this.beforeLastCardEnter()
      },
    },
  },

  mounted() {
    this.isMounted = true
  },

  methods: {
    ...mapActions({
      clearFilters: 'filter/clearFilters',
      getProducts: 'filter/getProducts',
    }),

    async beforeLastCardEnter() {
      await waitFor(this, () => this.isMounted)
      this.$emit('before-last-card-enter', this.$refs.cards)
    },

    handlePromoCardView(promoCard, position) {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignView,
        data: { identifier: this.formatPromoCardIdentifier(promoCard), position },
      })
    },

    handlePromoCardClick(promoCard, position) {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignClick,
        campaignIds: promoCard.campaignIds,
        data: { identifier: this.formatPromoCardIdentifier(promoCard), position },
      })
    },

    formatPromoCardIdentifier(promoCard) {
      return PROMO_CARD_EVENT_PREFIX + promoCard.identifier
    },

    loadMore() {
      this.getProducts({ after: this.pageInfo.endCursor })
    },
  },
}
</script>

<style lang="scss">
.products {
  position: relative;
  flex: 1 1 auto;
  align-self: flex-start;
  margin: 0 16px;

  @media ($mq-large) {
    margin: 0 14px;
    margin-left: 30px;
    padding-top: 30px;
  }

  @media (min-width: 1280px) {
    margin-right: 8px;
  }

  @media (min-width: 1440px) {
    margin-right: 14px;
  }

  &__no-results {
    position: relative;
    top: 50px;
    max-width: 280px;
    margin: 0 auto;
    font-size: 24px;
    text-align: center;
    color: $color-grey-mid-2;

    @media ($mq-large) {
      top: 280px;
      max-width: 500px;
    }

    h3 {
      font-size: 32px;
      color: $color-grey-dark-2;

      @media ($mq-large) {
        font-size: 40px;
      }
    }

    p {
      margin-top: 20px;
      font-size: 18px;
      line-height: 1.6;
      color: $color-grey-mid-3;

      @media ($mq-large) {
        font-size: 20px;
      }

      button {
        text-decoration: underline;
        text-underline-position: bottom;
        color: $color-green;
      }
    }
  }

  &--loading {
    min-height: 80vh;
  }

  &__load-more {
    text-align: center;
    margin-bottom: 4rem;
  }
  &__load-more-btn.button {
    width: auto;
  }
}

.no-products-fade {
  &-enter-active {
    transition: all 0.4s ease-in-out;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>

<i18n src="@/locales/filter_products.json">
</i18n>
